import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { useAuth } from "@/lib/auth-context";
import { supabase } from "@/lib/supabase";
import { isSafeNext } from "@/lib/safe-next";

// Replaces /app/auth/callback/route.ts. The OAuth code exchange and recovery-
// hash session restoration happen automatically inside supabase-js (the
// client is configured with detectSessionInUrl:true). Once useAuth resolves
// to a session, route to the right place:
//
//   1. If `?next=` is set AND passes isSafeNext, honor it.
//   2. Else if the user is a partner with no onboardedAt, send them to
//      /portal/profile?setup=1 (matches the legacy onboarding redirect).
//   3. Else send them to the role-based home.
//
// On error (URL hash carries error_description from Supabase, or session
// never appears), bounce to /login?error=...
export function AuthCallbackPage() {
  const { session, supabaseSession, loading } = useAuth();
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const [hashError, setHashError] = useState<string | null>(null);

  // Supabase puts OAuth/recovery errors in the URL fragment, e.g.
  // #error=access_denied&error_description=...&error_code=...
  useEffect(() => {
    const hash = window.location.hash.replace(/^#/, "");
    if (!hash) return;
    const params = new URLSearchParams(hash);
    const desc = params.get("error_description") ?? params.get("error");
    if (desc) setHashError(desc);
  }, []);

  useEffect(() => {
    if (hashError) {
      navigate(`/login?error=${encodeURIComponent(hashError)}`, { replace: true });
    }
  }, [hashError, navigate]);

  useEffect(() => {
    if (loading) return;

    if (!supabaseSession) {
      navigate("/login?error=Invalid+or+expired+link", { replace: true });
      return;
    }

    // Profile may still be hydrating even when supabaseSession resolves; wait
    // for the SessionDTO before deciding the onboarding branch.
    if (!session) return;

    const next = search.get("next");
    if (isSafeNext(next)) {
      navigate(next, { replace: true });
      return;
    }

    if (session.role === "partner" && !session.onboardedAt) {
      navigate("/portal/profile?setup=1", { replace: true });
      return;
    }

    const home = session.role === "internal" ? "/admin/dashboard" : "/portal/dashboard";
    navigate(home, { replace: true });
  }, [loading, supabaseSession, session, search, navigate]);

  useEffect(() => {
    // Belt-and-braces: ensure the URL hash/query is cleaned up even if
    // detectSessionInUrl already consumed the code.
    void supabase.auth.getSession();
  }, []);

  return (
    <div className="flex min-h-screen items-center justify-center text-sm text-muted-foreground">
      Signing you in…
    </div>
  );
}
